import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Ban, Copy, Pencil, Users } from 'lucide-react';
import { hrGetJob, hrCloseJob, hrJobApplications } from '../../lib/jobsApi';
import type { HrJob, HrTask, SubmissionType } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { hrErrorMessage } from '../../lib/tenancyErrorCopy';

/*
 * Velaigal /hr/jobs/:id — HR-only job management / detail view.
 *
 * A read-only mirror of the CreateJob wizard's task cards (rubric visible —
 * this route never renders for a candidate, only the owning recruiter),
 * plus the same status-scoped actions the /hr/jobs list row offers
 * (Edit / Copy link / Close / View applicants). Editing job details or
 * tasks still happens back on the wizard at /hr/jobs/:id/edit — this page
 * is a dashboard, not a second editor.
 *
 * hrGetJob doesn't return applicationCount (only the hrListJobs join does —
 * see hrJobs.ts), so the applicant count here comes from a second call to
 * hrJobApplications and just uses the row count.
 */

const STATUS_LABEL: Record<HrJob['status'], string> = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Closed',
};

const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  hosted_link: 'Hosted link',
  repo: 'Repository',
  video: 'Video',
  document: 'Document',
};

function originLabel(task: HrTask): string {
  if (task.origin === 'manual') return 'Manual';
  return task.editedAt ? 'AI · edited' : 'AI draft';
}

function copyPublicLink(jobId: string): void {
  const link = `${window.location.origin}/jobs/${jobId}`;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(link).then(
      () => toast.success('Public link copied.'),
      () => toast.error('Could not copy the link.'),
    );
  } else {
    toast.error('Could not copy — copy it manually from the address shown.');
  }
}

// Saffron is reserved for the "live" state (Published); draft/closed get the
// neutral hairline override — same rule ApplicationStatusChip follows in
// src/pages/JobSeeker/Applications.tsx.
const StatusChip: React.FC<{ status: HrJob['status'] }> = ({ status }) => {
  const isSaffron = status === 'published';
  return (
    <span
      className="chip"
      style={isSaffron ? undefined : { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: isSaffron ? 'var(--color-saffron-press)' : 'var(--color-ink-tertiary)' }}
      />
      {STATUS_LABEL[status]}
    </span>
  );
};

const InlineSpinner: React.FC<{ label: string }> = ({ label }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={{ borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }}
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading this job.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      This job isn&apos;t available.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      It may have been deleted, or you don&apos;t have access to it.
    </p>
    <Link to="/hr/jobs" className="btn-primary">
      Back to your jobs
    </Link>
  </div>
);

const LoadErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Couldn&apos;t load this job.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

const TaskCard: React.FC<{ task: HrTask; index: number }> = ({ task, index }) => (
  <article className="card">
    <span className="label-editorial block mb-1" style={{ color: 'var(--color-saffron)' }}>
      Task {String(index + 1).padStart(2, '0')}
    </span>
    <h3 className="font-display text-lg font-medium text-ink">{task.title}</h3>
    <p className="text-sm text-ink-secondary mt-2 leading-relaxed whitespace-pre-line">{task.brief}</p>
    <div className="flex flex-wrap items-center gap-2 mt-3">
      <span className="chip" style={{ backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}>
        {originLabel(task)}
      </span>
      {task.allowedSubmissionTypes.map((t) => (
        <span key={t} className="chip">
          {SUBMISSION_TYPE_LABELS[t]}
        </span>
      ))}
    </div>
    {task.evaluationCriteria.length > 0 && (
      <div className="mt-4 pt-4 border-t border-hairline">
        <span className="label-editorial block mb-2">Private scoring rubric — candidates never see this</span>
        <ul className="space-y-1">
          {task.evaluationCriteria.map((c, i) => (
            <li key={i} className="text-sm text-ink-secondary flex items-center justify-between gap-4">
              <span className="min-w-0 truncate">{c.criterion}</span>
              <span className="text-ink-tertiary flex-shrink-0">{c.weight}/10</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </article>
);

const JobManage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [loadNonce, setLoadNonce] = useState(0);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [job, setJob] = useState<HrJob | null>(null);
  const [tasks, setTasks] = useState<HrTask[]>([]);
  const [applicantCount, setApplicantCount] = useState(0);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [closing, setClosing] = useState(false);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load-by-id effect in CreateJob.tsx.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const jobRes = await hrGetJob(id);
        if (cancelled) return;
        setJob(jobRes.job);
        setTasks(jobRes.tasks);
        // The applicant count is best-effort — a failure here shouldn't
        // block the rest of the page from rendering.
        try {
          const appsRes = await hrJobApplications(id);
          if (!cancelled) setApplicantCount(appsRes.applications.length);
        } catch {
          if (!cancelled) setApplicantCount(0);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  const handleClose = async () => {
    if (!job || closing) return;
    setClosing(true);
    try {
      await hrCloseJob(job.id);
      setJob((prev) => (prev ? { ...prev, status: 'closed' } : prev));
      setConfirmingClose(false);
      toast.success('Job closed.');
    } catch (err) {
      toast.error(hrErrorMessage(err, 'Could not close this job.'));
    } finally {
      setClosing(false);
    }
  };

  if (loading) return <LoadingNotice />;
  if (loadError?.notFound) return <NotFoundNotice />;
  if (loadError) return <LoadErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
  if (!job) return null;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <StatusChip status={job.status} />
        <span className="text-sm text-ink-tertiary">
          {applicantCount} applicant{applicantCount === 1 ? '' : 's'}
        </span>
      </div>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-2">{job.title}</h1>
      <p className="text-xs text-ink-tertiary mb-8">
        {job.publishedAt ? `Published ${formatRelativeDate(job.publishedAt)}` : 'Not yet published'}
      </p>

      <div className="mb-10">
        <div className="flex items-center gap-2 flex-wrap">
          {job.status === 'draft' && (
            <Link to={`/hr/jobs/${job.id}/edit`} className="btn-secondary">
              <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
              Edit
            </Link>
          )}

          {job.status === 'published' && (
            <>
              <button type="button" className="btn-secondary" onClick={() => copyPublicLink(job.id)}>
                <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />
                Copy public link
              </button>
              <Link to={`/hr/jobs/${job.id}/applicants`} className="btn-secondary">
                <Users className="h-3.5 w-3.5" strokeWidth={1.75} />
                View applicants
              </Link>
              {confirmingClose ? (
                <>
                  <button type="button" className="btn-danger" onClick={() => void handleClose()} disabled={closing}>
                    {closing ? <InlineSpinner label="Closing…" /> : 'Confirm close'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmingClose(false)}
                    disabled={closing}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" className="btn-secondary" onClick={() => setConfirmingClose(true)}>
                  <Ban className="h-3.5 w-3.5" strokeWidth={1.75} />
                  Close
                </button>
              )}
            </>
          )}

          {job.status === 'closed' && (
            <Link to={`/hr/jobs/${job.id}/applicants`} className="btn-secondary">
              <Users className="h-3.5 w-3.5" strokeWidth={1.75} />
              View applicants
            </Link>
          )}
        </div>
        {confirmingClose && (
          <p className="mt-3 text-xs text-ink-tertiary">
            Closing stops new applications — candidates keep their submissions.
          </p>
        )}
      </div>

      <div className="card space-y-4 mb-10">
        <div>
          <span className="label-editorial block mb-1">Description</span>
          <p className="text-sm text-ink-secondary whitespace-pre-line">{job.description || '—'}</p>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="label-editorial block mb-1">Experience</span>
            <p className="text-ink">{job.experienceYearsMin}+ years</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Education</span>
            <p className="text-ink">{job.education || '—'}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Level</span>
            <p className="text-ink">{job.positionLevel || '—'}</p>
          </div>
          <div>
            <span className="label-editorial block mb-1">Salary</span>
            <p className="text-ink">{job.salaryDisplay || 'Hidden'}</p>
          </div>
        </div>
      </div>

      <div>
        <span className="label-editorial block mb-4">Tasks ({tasks.length})</span>
        <div className="space-y-4">
          {tasks.map((task, i) => (
            <TaskCard key={task.id} task={task} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default JobManage;
